import { orderBy } from 'lodash-es';

import {
  AllocationUnits,
  DisplayLogicLogicalModifier,
  Question,
  QuestionQuotaLogicalModifiers,
  QUESTION_FEATURE,
  QUESTION_TYPE,
  SurveyVariableQuotaType,
  SurveyVariable,
  SurveyWave,
} from '../types/domainModels';
import { getConceptTitle, isDemographicQuestion } from './questions';
import { getSurveyWaveTitle } from './surveyWaves';
import { OptionType } from 'types/internal';
import { ReactSelectValue } from '../types/forms';
import { stripHtml } from './richText';

export const CARRY_FORWARD_MATRIX_ANY_OPTION: ReactSelectValue<{
  id?: number;
  title: string;
}> = {
  label: 'Any Option',
  value: { title: '' },
};

export const DISPLAY_LOGIC_MODIFIER_OPTIONS: ReactSelectValue<DisplayLogicLogicalModifier>[] =
  [
    {
      label: 'equal to',
      value: 'is',
    },
    {
      label: 'either',
      value: 'should',
    },
    {
      label: 'not',
      value: 'isnt',
    },
  ];

export const QUESTION_QUOTA_LOGICAL_MODIFIER_OPTIONS: ReactSelectValue<QuestionQuotaLogicalModifiers>[] =
  [
    {
      label: 'At most',
      value: 'at_most',
    },
    {
      label: 'At least',
      value: 'at_least',
    },
    { label: 'All', value: 'all' },
    {
      label: 'None, or no',
      value: 'none',
    },
  ];

export const ALLOCATION_UNIT_OPTIONS: ReactSelectValue<AllocationUnits>[] = [
  { label: 'No Units', value: 'CUSTOM' },
  { label: 'Dollars ($)', value: 'DOLLARS' },
  { label: 'Percent (%)', value: 'PERCENT' },
];

export const QUESTION_TYPE_OPTIONS = [
  {
    label: 'Multiple Choice',
    value: QUESTION_TYPE.MULTIPLE_CHOICE,
  },
  {
    label: 'Open Ended',
    value: QUESTION_TYPE.OPEN_ENDED,
  },
  {
    label: 'Scale',
    value: QUESTION_TYPE.SCALE,
  },
  {
    label: 'Ranking',
    value: QUESTION_TYPE.RANKING,
  },
  {
    label: 'Matrix',
    value: QUESTION_TYPE.MATRIX,
  },
  {
    label: 'Gabor-Granger',
    value: QUESTION_TYPE.GABOR_GRANGER,
  },
  {
    label: 'Idea Presenter (Monadic)',
    value: QUESTION_TYPE.IDEA_PRESENTER,
  },
  {
    label: 'Allocation',
    value: QUESTION_TYPE.ALLOCATION,
  },
];

export const SURVEY_VARIABLE_QUOTA_TYPE_OPTIONS: ReactSelectValue<SurveyVariableQuotaType>[] =
  [
    {
      label: 'At most',
      value: 'at_most',
    },
    { label: 'All', value: 'all' },
    {
      label: 'None, or no',
      value: 'none',
    },
  ];

export function getActiveQuestionOptions<T extends Question>(
  questionOptions: ReactSelectValue<T>[],
) {
  return questionOptions.filter((q) => q.value.isActive);
}

export const ALLOCATION_CARRY_FORWARD_TYPE_OPTIONS: ReactSelectValue<QUESTION_FEATURE>[] =
  [
    {
      label: 'Options displayed',
      value: QUESTION_FEATURE.CARRY_FORWARD_DISPLAYED,
    },
    {
      label: 'Options allocated',
      value: QUESTION_FEATURE.CARRY_FORWARD_SELECTED,
    },
    {
      label: 'Options not allocated',
      value: QUESTION_FEATURE.CARRY_FORWARD_NOT_SELECTED,
    },
  ];

export function getCarryForwardTypeOptions(
  selectedQuestion?: Question,
): ReactSelectValue<QUESTION_FEATURE>[] {
  const labelWord =
    selectedQuestion?.questionTypeId === QUESTION_TYPE.MATRIX
      ? 'Statements'
      : 'Options';

  return [
    {
      label: `${labelWord} displayed`,
      value: QUESTION_FEATURE.CARRY_FORWARD_DISPLAYED,
    },
    {
      label: `${labelWord} selected`,
      value: QUESTION_FEATURE.CARRY_FORWARD_SELECTED,
    },
    {
      label: `${labelWord} not selected`,
      value: QUESTION_FEATURE.CARRY_FORWARD_NOT_SELECTED,
    },
  ];
}

/**
 * Returns a single option for a select input from an API question concept.
 */
export function getConceptOption<ConceptType extends { description: string }>({
  concept,
  index,
}: {
  concept: ConceptType;
  index?: number;
}): ReactSelectValue<ConceptType> {
  return {
    label: getConceptTitle({ concept, index }),
    value: concept,
  };
}

/**
 * Returns concepts for a select dropdown from a set of question concepts.
 */
export function getConceptOptions<
  ConceptType extends { description: string; id: number },
>({
  concepts,
  filterFn,
}: {
  concepts: ConceptType[];
  filterFn?: (concept: ConceptType) => boolean;
}): ReactSelectValue<ConceptType>[] {
  const resolvedFilterFn = filterFn ?? (() => true);

  return concepts.filter(resolvedFilterFn).map((concept, index) => {
    return getConceptOption({ concept, index });
  });
}

/**
 * Returns a single option for a select input from an API question option.
 */
export function getOptionOption<OptionType extends { title: string }>({
  option,
}: {
  option: OptionType;
}): ReactSelectValue<OptionType> {
  return {
    label: option.title,
    value: option,
  };
}

/**
 * Returns options for a select dropdown from a set of question options.
 */
export function getOptionOptions<
  OptionType extends {
    isActive: boolean;
    sort: number;
    title: string;
  },
>({
  filterFn,
  options,
}: {
  filterFn?: (option: OptionType) => boolean;
  options: OptionType[] | [];
}): ReactSelectValue<OptionType>[] {
  const resolvedFilterFn = filterFn ?? (() => true);

  return options
    .filter(resolvedFilterFn)
    .map((option) => {
      return getOptionOption({ option });
    })
    .filter((o) => o.value.isActive);
}

export function getOptionTypes({
  isAdmin,
  questionType,
}: {
  isAdmin: boolean;
  questionType: QUESTION_TYPE | undefined;
}): ReactSelectValue<OptionType>[] {
  if (questionType === QUESTION_TYPE.OPEN_ENDED) {
    const optionTypes: ReactSelectValue<OptionType>[] = [
      {
        label: 'Text',
        value: 'text',
      },
      {
        label: 'Number',
        value: 'number',
      },
      {
        label: 'Email',
        value: 'email',
      },
      {
        label: 'Zip code',
        value: 'zip',
      },
    ];

    if (isAdmin) {
      optionTypes.push({
        label: 'Audio / Video',
        value: 'audio-video',
      });
    }

    return orderBy(optionTypes, (ot) => ot.label);
  }

  return [
    {
      label: 'Image',
      value: 'image',
    },
    {
      label: 'Text',
      value: 'text',
    },
  ];
}

export function getQuestionOption<
  QuestionType extends {
    isDemographic: boolean;
    isStandard: boolean;
    sort: number;
    title: string;
  },
>({ question }: { question: QuestionType }): ReactSelectValue<QuestionType> {
  // We apply an "D" to demographic questions to differentiate them from custom user questions.
  const sortPrefix = isDemographicQuestion(question) ? 'D' : '';
  const questionPrefix = question.sort ? `${sortPrefix}${question.sort}) ` : '';

  return {
    label: `${questionPrefix}${stripHtml(question.title)}`,
    value: question,
  };
}

export function getWave(
  variable: SurveyVariable,
): ReactSelectValue<SurveyVariable> {
  return {
    label: variable.title,
    value: variable,
  };
}

export function getQuestionOptions<T extends Question>({
  filterFn,
  includeInactiveQuestions = false,
  questions,
}: {
  filterFn?: (question: T) => boolean;
  includeInactiveQuestions?: boolean;
  questions: T[];
}): ReactSelectValue<T>[] {
  const resolvedFilterFn = filterFn ?? (() => true);
  const questionOptions = questions.filter(resolvedFilterFn).map((question) => {
    return getQuestionOption({ question });
  });

  return includeInactiveQuestions
    ? questionOptions
    : getActiveQuestionOptions(questionOptions);
}

export function getSurveyWaveOption(wave: SurveyWave) {
  return {
    label: getSurveyWaveTitle(wave),
    value: wave,
  };
}

export function getAllocationUnitOption(
  units: AllocationUnits,
): ReactSelectValue<AllocationUnits> {
  const option = ALLOCATION_UNIT_OPTIONS.find((opt) => opt.value === units);
  if (!option) {
    throw new Error(`Invalid allocation unit: ${units}`);
  }
  return option;
}
